/**
 * The report workspace at /matters/:ref/workspace (docs/ux/workspace.md):
 * the doctor drafts, dictates and decides; QA reviews and proposes; each
 * reads the other's turn. One query holds the document and its version; every
 * write carries that version and returns the whole view. The outline rail
 * steps through what needs attention; the header offers the persona's two
 * events with the engine's answer.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { ApiError, reasonOf } from '@/app/api';
import { say } from '@/app/announcer';
import { useOnline } from '@/app/online';
import { personaHome, useSession } from '@/app/session';
import type { SuggestionGroupInput, WorkspaceEventKind, WorkspaceEventOption, WorkspaceView } from '@/app/types';
import { useIsPhone, useViewportWidth } from '@/app/viewport';
import { StateChip } from '@/components/matter/StateChip';
import { StatusChip } from '@/components/matter/StatusChip';
import { PageHeader } from '@/components/shell/PageHeader';
import { useShellWidth } from '@/components/shell/context';
import { EmptyState, ErrorPanel } from '@/components/shell/states';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { pluralise } from '@/lib/format';
import { MatterNotFound } from '../matter/panels';
import { attentionGroups, doctorSentence, finalisedSentence, formatOutcomeSentence, modeFor, movedToast, patientTitle, primaryEvent, questionNumberOf, readReason, unreachableCopy, waitingSentence, type AttentionGroup, type AttentionKey } from './describe';
import { BulkDecisionDialog, CompleteQaDialog, type BulkDecision } from './dialogs';
import { DictationHelp } from './DictationHelp';
import { EventCluster } from './EventCluster';
import { useFolding } from './folding';
import { WorkingNotes } from './lists';
import { AttentionBar, OutlineRail, type Stepper } from './OutlineRail';
import { InterimPreview, type BoxActions, type SaveOutcome } from './ReportBox';
import { SectionCard, type BoxContext } from './SectionCard';
import type { ProofreadingSettings } from './proofreading';
import { addDictionaryWord, planEdits, useDictation, useFormat, useWorkspace, useWorkspaceWrite, workspaceKey, workspaceWrites, type Caret, type WriteVars } from './useWorkspace';

const HIGHLIGHT_MS = 1200;

export function WorkspacePage() {
  const { ref = '' } = useParams();
  useShellWidth('full');
  const { me } = useSession();
  const online = useOnline();
  const phone = useIsPhone();
  const width = useViewportWidth();
  const rail = width >= 1200;
  const client = useQueryClient();
  const query = useWorkspace(ref);
  const view = query.data;
  const write = useWorkspaceWrite(ref);
  const formatter = useFormat(ref);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [busyEvent, setBusyEvent] = useState<WorkspaceEventKind | null>(null);
  // Proofreading (D-0059 amendment): the page switch is scoped to the open report; leaving restores the standing preference.
  const [proofChoice, setProofChoice] = useState<boolean | null>(null);
  const [issueCounts, setIssueCounts] = useState<Record<string, number>>({});
  useEffect(() => { setProofChoice(null); setIssueCounts({}); }, [ref]);
  const reportIssues = useCallback((boxId: string, count: number) => setIssueCounts((c) => (c[boxId] === count ? c : { ...c, [boxId]: count })), []);
  // The linter loads after the page (a 16 MB WebAssembly binary on a cold cache); until a box reports it ready the switch says so.
  const [linterReady, setLinterReady] = useState(false);
  const markLinterReady = useCallback(() => setLinterReady(true), []);
  const [alert, setAlert] = useState<string | null>(null);
  const [promoted, setPromoted] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState<BulkDecision | null>(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const [focusTokens, setFocusTokens] = useState<Record<string, number>>({});
  const [cursor, setCursor] = useState<Partial<Record<AttentionKey, number>>>({});
  const [currentSection, setCurrentSection] = useState<string | null>(null);
  const completeRef = useRef<HTMLParagraphElement>(null);
  const versionRef = useRef(0);
  versionRef.current = view?.version ?? 0;

  const sectionKeys = useMemo(() => view?.sections.map((s) => s.key) ?? [], [view]);
  const folding = useFolding(ref, sectionKeys);

  /* ------------------------------------------------------- dictation */
  const dictation = useDictation(ref, {
    onFinal: (m) => {
      client.setQueryData<WorkspaceView>(workspaceKey(ref), (prev) => prev ? {
        ...prev,
        version: m.version,
        sections: prev.sections.map((s) => ({
          ...s,
          boxes: s.boxes.map((b) => (b.id === m.boxId ? { ...b, text: m.text, records: m.records, heard: m.heard ?? b.heard } : b)),
          notes: s.notes.boxId === m.boxId ? { ...s.notes, text: m.text, records: m.records, heard: m.heard ?? s.notes.heard } : s.notes,
        })),
      } : prev);
      if (m.promotedFromInterim) setPromoted((p) => new Set(p).add(m.boxId));
      say(`Landed ${pluralise(m.text.split(/\s+/).filter(Boolean).length, 'word')}.`);
    },
    onFormatted: (m) => {
      client.setQueryData<WorkspaceView>(workspaceKey(ref), (prev) => prev ? {
        ...prev,
        version: m.version,
        sections: prev.sections.map((s) => ({
          ...s,
          boxes: s.boxes.map((b) => (b.id === m.boxId ? { ...b, text: m.text, records: m.records, heard: m.heard ?? b.heard } : b)),
          notes: s.notes.boxId === m.boxId ? { ...s.notes, text: m.text, records: m.records, heard: m.heard ?? s.notes.heard } : s.notes,
        })),
      } : prev);
      say('Formatted the passage you just dictated. Undo is in the Changes to your words list.');
    },
    onError: (message) => setAlert(message),
    onDone: () => { void client.invalidateQueries({ queryKey: workspaceKey(ref) }); say('Dictation stopped.'); },
  });

  /* --------------------------------------------------------- writes */
  const run = useCallback(async (vars: WriteVars, id: string | null): Promise<boolean> => {
    setBusyId(id);
    try {
      await write.mutateAsync(vars);
      return true;
    } catch {
      return false;
    } finally {
      setBusyId(null);
    }
  }, [write]);

  const actions: BoxActions = useMemo(() => ({
    save: async (boxId, text): Promise<SaveOutcome> => {
      try {
        await write.mutateAsync(workspaceWrites(versionRef.current).saveText(boxId, text, true));
        return 'saved';
      } catch (err) {
        return err instanceof ApiError && err.status === 409 ? 'stale' : 'failed';
      }
    },
    undo: (boxId, changeId) => void run(workspaceWrites(versionRef.current).undoStyle(boxId, changeId), boxId),
    format: async (boxId, range) => {
      setBusyId(boxId);
      try {
        const envelope = await formatter.mutateAsync({ boxId, version: versionRef.current, range });
        if (envelope.outcome === 'applied') { say(formatOutcomeSentence('applied')); return { outcome: 'applied' }; }
        if (envelope.outcome === 'rejected') { say(formatOutcomeSentence('rejected', envelope.reason)); return { outcome: 'rejected', reason: envelope.reason }; }
        say(formatOutcomeSentence('unchanged'));
        return { outcome: 'unchanged' };
      } catch {
        return { outcome: 'failed' };
      } finally {
        setBusyId(null);
      }
    },
    confirmHeard: (boxId, id) => run(workspaceWrites(versionRef.current).confirmHeard(boxId, id), boxId).then((ok) => { if (ok) say('Mark cleared. The word stands as written.'); return ok; }),
    proofread: (boxId, change) => run(workspaceWrites(versionRef.current).proofread(boxId, change), boxId).then((ok) => { if (ok) say(`Applied ${change.after ? `"${change.after}"` : 'the removal'}. Undo is in the Changes to your words list.`); return ok; }),
    addWord: async (word) => {
      try {
        const result = await addDictionaryWord(ref, word);
        client.setQueryData<WorkspaceView>(workspaceKey(ref), (prev) => (prev ? { ...prev, proofreading: { ...prev.proofreading, dictionary: [...new Set([...prev.proofreading.dictionary, ...result.dictionary])] } } : prev));
        say(`Added "${word}" to the dictionary.`);
        return true;
      } catch (err) {
        toast.error(`Could not add "${word}" to the dictionary. ${reasonOf(err) ?? 'Nothing changed.'}`);
        return false;
      }
    },
    decide: (id, decision, reason) => void run(workspaceWrites(versionRef.current).decide(id, decision, reason || undefined), id).then((ok) => { if (ok) say(decision === 'accept' ? 'Change accepted.' : 'Change declined.'); }),
    revert: (id) => void run(workspaceWrites(versionRef.current).revert(id), id).then((ok) => { if (ok) say('Reverted the applied change.'); }),
    ask: (boxId, question, about) => void run(workspaceWrites(versionRef.current).ask(boxId, question, about), boxId).then((ok) => { if (ok) say('Question sent to the doctor.'); }),
    address: (id, answer) => void run(workspaceWrites(versionRef.current).address(id, answer), id).then((ok) => { if (ok) say('Question closed.'); }),
    acceptChange: (boxId) => void run(workspaceWrites(versionRef.current).acceptChange(boxId), boxId).then((ok) => { if (ok) say('Doctor\'s change accepted.'); }),
    plan: (boxId, text, groups: SuggestionGroupInput[] | null) => planEdits(ref, boxId, text, groups),
    propose: (boxId, text, groups) => run(workspaceWrites(versionRef.current).propose(boxId, text, groups), boxId),
    dictate: (boxId: string, caret: Caret) => { dictation.start(boxId, caret); },
    stop: () => dictation.stop(),
    decideAll: (sectionKey, decision) => {
      const section = view?.sections.find((s) => s.key === sectionKey);
      if (!section) return;
      const open = section.boxes.flatMap((b) => b.suggestions);
      setBulk({ sectionKey, sectionLabel: section.label, decision, count: open.length, withoutReason: open.filter((s) => !s.reason).length });
    },
  }), [write, run, ref, dictation, view]);

  async function fireEvent(e: WorkspaceEventOption): Promise<boolean> {
    if (!view) return false;
    setBusyEvent(e.kind);
    try {
      const next = await write.mutateAsync(workspaceWrites(versionRef.current).event(e.kind, e.label));
      toast.success(movedToast(e.kind, ref, next.matterStatus));
      requestAnimationFrame(() => document.getElementById('page-title')?.focus());
      return true;
    } catch {
      return false;
    } finally {
      setBusyEvent(null);
    }
  }

  function onEvent(e: WorkspaceEventOption) {
    if (e.kind === 'qa_completes') { setCompleteOpen(true); return; }
    void fireEvent(e);
  }

  /* ------------------------------------------------------ stepping */
  const reveal = useCallback((boxId: string) => {
    if (!view) return;
    const section = view.sections.find((s) => s.boxes.some((b) => b.id === boxId));
    if (!section) return;
    const box = section.boxes.find((b) => b.id === boxId)!;
    folding.reveal(section.key, section.kind === 'questions' ? questionNumberOf(box) : null);
    setHighlighted(boxId);
    setFocusTokens((t) => ({ ...t, [boxId]: (t[boxId] ?? 0) + 1 }));
    window.setTimeout(() => setHighlighted((h) => (h === boxId ? null : h)), HIGHLIGHT_MS);
  }, [view, folding]);

  const stepper: Stepper = useMemo(() => ({
    cursor,
    step: (group: AttentionGroup, direction) => {
      const n = group.boxIds.length;
      if (n === 0) return;
      const current = cursor[group.key];
      const next = current === undefined ? (direction === 1 ? 0 : n - 1) : (current + direction + n) % n;
      setCursor((c) => ({ ...c, [group.key]: next }));
      const boxId = group.boxIds[next]!;
      reveal(boxId);
      const box = view?.sections.flatMap((s) => s.boxes).find((b) => b.id === boxId);
      say(`${next + 1} of ${n} ${group.many.toLowerCase()}: ${box?.label ?? ''}.`);
    },
  }), [cursor, reveal, view]);

  // Stepping cursors reset when the groups change shape (a decision removes a box from its group).
  const groupShape = view ? attentionGroups(view).map((g) => `${g.key}:${g.boxIds.length}`).join('|') : '';
  useEffect(() => { setCursor({}); }, [groupShape]);

  const jumpToSection = useCallback((key: string) => {
    folding.reveal(key, null);
    requestAnimationFrame(() => document.getElementById(`section-${key}`)?.scrollIntoView({ block: 'start' }));
    const firstBox = view?.sections.find((s) => s.key === key)?.boxes.find((b) => b.editable);
    if (firstBox) setFocusTokens((t) => ({ ...t, [firstBox.id]: (t[firstBox.id] ?? 0) + 1 }));
  }, [folding, view]);

  // The outline marks the section in view.
  useEffect(() => {
    if (!view || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (visible) setCurrentSection((visible.target as HTMLElement).dataset.sectionKey ?? null);
    }, { rootMargin: '-100px 0px -60% 0px' });
    for (const s of view.sections) { const el = document.getElementById(`section-${s.key}`); if (el) io.observe(el); }
    return () => io.disconnect();
  }, [view]);

  /* ------------------------------------------------------ rendering */
  const home = personaHome(me?.persona);
  if (query.isError && query.error instanceof ApiError && query.error.status === 404) {
    return (<><PageHeader title={ref} documentTitle={ref} /><MatterNotFound matterRef={ref} home={home} /></>);
  }
  if (query.isError && !view) {
    return (<><PageHeader title={ref} documentTitle={ref} /><ErrorPanel className="mt-6" message="The workspace could not be loaded." onRetry={() => void query.refetch()} /></>);
  }
  if (!view) return <LoadingWorkspace ref_={ref} />;

  if (!view.reachable) {
    const copy = unreachableCopy(view);
    return (
      <>
        <PageHeader title={ref} documentTitle={`${ref} workspace`} />
        <EmptyState className="mt-6" title={copy.title} description={copy.detail} action={<Button variant="outline" asChild><Link to={`/matters/${encodeURIComponent(ref)}?tab=report`}>Open the case hub</Link></Button>} />
      </>
    );
  }

  const mode = modeFor(view);
  const reason = readReason(view);
  const title = patientTitle(view);
  // The page switch overrides the standing preference for the open report only; nothing is rendered where the box is read only.
  const proofOn = mode === 'edit' && (proofChoice ?? view.proofreading.defaultOn);
  const totalIssues = Object.values(issueCounts).reduce((a, b) => a + b, 0);
  const proofSettings: ProofreadingSettings = { on: proofOn, dictionary: view.proofreading.dictionary, userKey: me?.sub ?? 'anonymous', canAddWord: view.actorRole === 'doctor', report: reportIssues, onReady: markLinterReady };
  const mine = view.waitingOn === view.actorRole;
  const complete = view.status === 'qa_complete';
  const primary = primaryEvent(view);
  const emptyLine = view.unanswered > 0 && !complete ? `${pluralise(view.unanswered, 'box is', 'boxes are')} empty` : null;

  const ctx: BoxContext = {
    role: view.actorRole, mode, matterStatus: view.matterStatus, offline: !online, phone, dictation: dictation.state, dictationMode: view.dictation.mode, formattingMode: view.formatting.mode, autoFormatting: view.formatting.auto, proofreading: proofSettings,
    actions, busyId, highlighted, readReason: reason, promoted, onDismissPromoted: (boxId) => setPromoted((p) => { const n = new Set(p); n.delete(boxId); return n; }), focusTokens,
  };

  const refLink = <Link to={`/matters/${encodeURIComponent(ref)}?tab=report`} aria-label={`${ref}, open the case hub`} className="tabular text-sm font-semibold text-beam-signature underline-offset-2 hover:underline">{ref}</Link>;

  const outlineProps = { view, currentSection, onSection: jumpToSection, onBox: reveal, allFolded: folding.allFolded, onCollapseAll: folding.collapseAll, onExpandAll: folding.expandAll, stepper };

  // The compact strip belongs to the rail layout; under 1200 px the attention bar is the sticky helper and the strip would paint over it (docs/ux/workspace.md, section 4).
  return (
    <>
      <PageHeader
        title={title}
        documentTitle={`${ref} workspace`}
        description={<span className="flex flex-wrap items-center gap-x-3 gap-y-1">{refLink}<span className="text-beam-text-soft">{doctorSentence(view)}</span></span>}
        meta={(
          <>
            <StatusChip status={view.matterStatus} size="md" emphasis={mine && !complete ? 'mine' : undefined} />
            {view.currentRound > 0 ? <span className="tabular">Round {view.currentRound}</span> : null}
            <span>{waitingSentence(view)}</span>
            {emptyLine ? <span>{emptyLine}</span> : null}
            <span>{finalisedSentence(view.finalisedAt)}</span>
            {/* In edit mode the chip is the way to the dictation commands; read only, it stays a chip and only says when the source is synthetic. */}
            {mode === 'edit' ? <DictationHelp mode={view.dictation.mode} phone={phone} /> : view.dictation.mode === 'synthetic' && !complete ? <StateChip label="Dictation: synthetic" tone="warning" size="sm" /> : null}
            {mode === 'edit' ? (
              <span className="flex flex-wrap items-center gap-2" data-slot="proofreading-switch">
                <Switch checked={proofOn} onCheckedChange={(on) => { setProofChoice(on); say(on ? 'Proofreading on' : 'Proofreading off'); }} aria-label="Proofreading" className={phone ? 'scale-125' : undefined} />
                <span className="text-sm" data-slot="proofreading-state">{proofOn ? (!linterReady ? 'Proofreading: loading' : totalIssues > 0 ? `Proofreading: ${pluralise(totalIssues, 'suggestion')}` : 'Proofreading') : 'Proofreading off'}</span>
                {!proofOn ? <span className="text-xs text-beam-text-muted">{view.actorRole === 'doctor' ? 'Off for this report. Your standing preference is on the registry.' : 'Off for this report.'}</span> : null}
              </span>
            ) : null}
          </>
        )}
        actions={<EventCluster view={view} offline={!online} busy={busyEvent} onEvent={onEvent} phone={phone} />}
        compact={rail ? (
          <>
            <span className="flex min-w-0 items-center gap-3"><span className="truncate text-base font-semibold">{title}</span><span className="tabular hidden text-sm md:inline">{ref}</span><StatusChip status={view.matterStatus} size="sm" /></span>
            {primary && !complete ? <Button size="sm" className="h-8" disabled={!online || busyEvent !== null} aria-describedby={`engine-${primary.kind}`} onClick={() => onEvent(primary)}>{primary.label}</Button> : null}
          </>
        ) : undefined}
      />
      {phone && view.actorRole === 'qa' && mode === 'edit' ? <p data-slot="phone-qa-sentence" className="mt-3 text-[13px] text-beam-text-muted">Proposing edits, grouping and completing QA happen on a desktop.</p> : null}

      {complete ? <p ref={completeRef} tabIndex={-1} data-slot="complete-sentence" className="mt-3 text-[13px] text-beam-text-muted outline-none">This report is complete. Nothing here can be edited; the preview and export remain.</p> : null}
      {alert ? (
        <Alert role="alert" variant="destructive" className="mt-4" data-slot="page-alert">
          <AlertDescription className="flex flex-wrap items-center justify-between gap-2"><span>{alert}</span><Button size="sm" variant="ghost" className="h-8" onClick={() => setAlert(null)}>Dismiss</Button></AlertDescription>
        </Alert>
      ) : null}

      {!rail ? <div className="mt-4"><AttentionBar {...outlineProps} /></div> : null}

      <div className={rail ? 'mx-auto mt-6 grid max-w-[1204px] grid-cols-[280px_minmax(0,900px)] justify-center gap-6 xl:grid-cols-[280px_900px] [@media(max-width:1399px)]:grid-cols-[260px_minmax(0,860px)]' : 'mt-4 flex flex-col gap-6'}>
        {rail ? <OutlineRail {...outlineProps} /> : null}
        <div data-slot="report-column" className="flex min-w-0 flex-col gap-6">
          {view.sections.filter((s) => !s.parent).map((s) => {
            // A group (the schedule of documents) carries its children inside its own card, so the structure the
            // template defines is the structure the doctor sees; each child keeps its own id, fold and notes.
            const children = view.sections.filter((c) => c.parent === s.key);
            const notesFor = (section: typeof s) => section.kind === 'fields' ? null : (
              <WorkingNotes
                notes={section.notes}
                sectionKey={section.key}
                role={view.actorRole}
                offline={!online}
                mode={complete ? 'read' : mode}
                dictating={dictation.state.phase !== 'idle' && dictation.state.boxId === section.notes.boxId ? dictation.state.phase : 'idle'}
                onSave={async (text) => (await actions.save(section.notes.boxId, text)) === 'saved'}
                onDictate={(caret) => actions.dictate(section.notes.boxId, caret)}
                onStop={actions.stop}
                interim={dictation.state.phase !== 'idle' && dictation.state.boxId === section.notes.boxId && (dictation.state.phase === 'listening' || dictation.state.phase === 'finishing') ? <InterimPreview text={section.notes.text} caret={dictation.state.caret} interim={dictation.state.interim} /> : null}
                phone={phone}
              />
            );
            return (
              <SectionCard
                key={s.key}
                section={s}
                view={view}
                folding={folding}
                ctx={ctx}
                notes={notesFor(s)}
                nested={children.length > 0 ? children.map((c) => (
                  <SectionCard key={c.key} section={c} view={view} folding={folding} ctx={ctx} notes={notesFor(c)} level={3} />
                )) : undefined}
              />
            );
          })}
          <p className="text-xs text-beam-text-muted">Interim transcripts are shown while you speak and are never saved. Working notes are stored separately from the report body.</p>
        </div>
      </div>

      <BulkDecisionDialog
        pending={bulk}
        onOpenChange={(o) => { if (!o) setBulk(null); }}
        onConfirm={async (p) => {
          const ok = await run(workspaceWrites(versionRef.current).decideAll(p.sectionKey, p.decision), p.sectionKey);
          if (ok) say(p.decision === 'accept' ? `Accepted ${pluralise(p.count, 'suggestion')}.` : `Declined ${pluralise(p.count, 'suggestion')}.`);
          return ok;
        }}
      />
      <CompleteQaDialog
        open={completeOpen}
        onOpenChange={setCompleteOpen}
        matterRef={ref}
        unanswered={view.unanswered}
        today={new Date().toISOString()}
        onConfirm={async () => {
          const e = view.events.find((x) => x.kind === 'qa_completes');
          if (!e) return false;
          const ok = await fireEvent(e);
          if (ok) requestAnimationFrame(() => completeRef.current?.focus());
          return ok;
        }}
      />
    </>
  );
}

function LoadingWorkspace({ ref_ }: { ref_: string }) {
  return (
    <>
      <PageHeader title={ref_} documentTitle={`${ref_} workspace`} description={<Skeleton className="h-4 w-64" />} meta={<Skeleton className="h-7 w-80" />} actions={<div className="flex gap-2"><Skeleton className="h-9 w-32" /><Skeleton className="h-9 w-28" /></div>} />
      <div className="mx-auto mt-6 grid max-w-[1204px] gap-6 lg:grid-cols-[280px_minmax(0,900px)]">
        <div className="hidden flex-col gap-2 lg:flex">{Array.from({ length: 8 }, (_, i) => <Skeleton key={i} className="h-8 rounded-md" />)}</div>
        <div className="flex flex-col gap-6"><Skeleton className="h-36 rounded-xl" /><Skeleton className="h-72 rounded-xl" /><Skeleton className="h-48 rounded-xl" /></div>
      </div>
    </>
  );
}

